'use client'

import {
    getOrganization,
    Organization,
    updateSubscription,
} from '@/lib/api/organization/organization-api'
import { getFileUrl } from '@/lib/utils/file-url'
import {
    formatPlanPrice,
    getPlans,
    Plan,
    RECURRENCE_LABELS,
} from '@/lib/api/plans/plan-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdArrowForward,
    MdBusiness,
    MdCalendarToday,
    MdEdit,
    MdEmail,
    MdGroups,
    MdLanguage,
    MdPayment,
    MdPhone,
    MdSave,
    MdStorefront,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const formatCnpj = (cnpj?: string | null): string => {
    if (!cnpj) return '-'
    const digits = cnpj.replace(/\D/g, '')
    if (digits.length !== 14) return cnpj
    return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5',
    )
}

const formatCpf = (cpf?: string | null): string => {
    if (!cpf) return '-'
    const digits = cpf.replace(/\D/g, '')
    if (digits.length !== 11) return cpf
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
}

const SEGMENT_LABELS: Record<string, string> = {
    HEALTH: 'Saúde',
    BEAUTY: 'Beleza',
    FITNESS: 'Fitness',
    PET_SHOP: 'Pet Shop',
    DIRECT_SALE: 'Venda (sem orçamento)',
    QUOTE_BASED_SALE: 'Venda (com orçamento)',
    OTHER: 'Outro',
}

interface OrgCompany {
    id: number
    name: string
    tradingName?: string
    cnpj?: string
    active?: boolean
    segment?: string
    email?: string
    phone?: string
    city?: string
    state?: string
    _count?: {
        users: number
    }
}

export default function OrganizationDetailPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [org, setOrg] = useState<Organization | null>(null)
    const [loading, setLoading] = useState(true)
    const [plans, setPlans] = useState<Plan[]>([])
    const [editingSubscription, setEditingSubscription] = useState(false)
    const [savingSubscription, setSavingSubscription] = useState(false)
    const [subForm, setSubForm] = useState({
        planId: 0,
        dueDay: 0,
        status: '',
    })

    useEffect(() => {
        if (id) {
            loadOrganization()
        }
    }, [id])

    const loadOrganization = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getOrganization(parseInt(id, 10))
            if (response.success && response.result) {
                setOrg(response.result)
            } else {
                toast.error(
                    response.error || 'Erro ao carregar organização',
                )
                navigate('/organizations')
            }
        } catch {
            toast.error('Erro ao carregar organização')
            navigate('/organizations')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        getPlans(true).then((res) => {
            if (res.success && Array.isArray(res.result)) {
                setPlans(res.result)
            }
        }).catch(() => { })
    }, [])

    const handleEditSubscription = () => {
        if (!org?.subscription) return
        setSubForm({
            planId: org.subscription.planId,
            dueDay: org.subscription.dueDay,
            status: org.subscription.status,
        })
        setEditingSubscription(true)
    }

    const handleSaveSubscription = async () => {
        if (!org?.subscription) return

        setSavingSubscription(true)
        try {
            const response = await updateSubscription(org.subscription.id, {
                planId: subForm.planId,
                dueDay: subForm.dueDay,
                status: subForm.status,
            })
            if (response.success) {
                toast.success('Assinatura atualizada com sucesso')
                setEditingSubscription(false)
                loadOrganization()
            } else {
                toast.error(response.error || 'Erro ao atualizar assinatura')
            }
        } catch {
            toast.error('Erro ao atualizar assinatura')
        } finally {
            setSavingSubscription(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando organização...
                </p>
            </div>
        )
    }

    if (!org) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdBusiness className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Organização não encontrada
                    </h3>
                    <p className="text-sm text-base-content/60">
                        A organização que você está procurando não existe
                    </p>
                </div>
                <button
                    onClick={() => navigate('/organizations')}
                    className="btn btn-secondary"
                >
                    Voltar para Organizações
                </button>
            </div>
        )
    }

    const companies = (org.companies || []) as OrgCompany[]
    /** Conta pessoal: o titular É a pessoa, não há razão social nem CNPJ. */
    const isIndividual = org.personType === 'FISICA'

    return (
        <div className="flex flex-col gap-6 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/organizations')}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 hidden md:flex rounded-xl bg-gradient-to-br from-secondary to-secondary/70 items-center justify-center shadow-lg overflow-hidden">
                            {org.logoUrl ? (
                                <img
                                    src={getFileUrl(org.logoUrl)}
                                    alt={org.name}
                                    className="w-10 h-10 object-contain"
                                />
                            ) : (
                                <MdBusiness className="text-2xl text-white" />
                            )}
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                    {org.name}
                                </h1>
                                <span
                                    className={`badge badge-sm ${org.active
                                            ? 'badge-success'
                                            : 'badge-error'
                                        }`}
                                >
                                    {org.active ? 'Ativa' : 'Inativa'}
                                </span>
                            </div>
                            <p className="text-sm text-base-content/60 mt-1">
                                Detalhes da organização
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Card de Dados Gerais */}
            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                {/* Datas */}
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 text-sm text-base-content/70">
                            <MdCalendarToday />
                            <span>
                                Criada em{' '}
                                {format(
                                    new Date(org.createdAt),
                                    "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                    { locale: ptBR },
                                )}
                            </span>
                        </div>
                        {org.updatedAt !== org.createdAt && (
                            <div className="flex items-center gap-2 text-sm text-base-content/70">
                                <span>
                                    Atualizada em{' '}
                                    {format(
                                        new Date(org.updatedAt),
                                        "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                        { locale: ptBR },
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                        <MdBusiness className="text-secondary" />
                        Dados Gerais
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Conta pessoal não tem razão social nem CNPJ, e o CPF
                            nem aparecia aqui: a tela mostrava um CNPJ vazio como
                            se faltasse cadastro. */}
                        <InfoItem
                            label={isIndividual ? 'Nome' : 'Razão Social'}
                            value={org.name}
                        />
                        {!isIndividual && (
                            <InfoItem
                                label="Nome Fantasia"
                                value={org.tradingName}
                            />
                        )}
                        {isIndividual ? (
                            <InfoItem label="CPF" value={formatCpf(org.cpf)} />
                        ) : (
                            <InfoItem
                                label="CNPJ"
                                value={formatCnpj(org.cnpj)}
                            />
                        )}
                        <InfoItem
                            label="Email"
                            value={org.email}
                            icon={
                                <MdEmail className="text-base text-base-content/40" />
                            }
                        />
                        <InfoItem
                            label="Telefone"
                            value={org.phone}
                            icon={
                                <MdPhone className="text-base text-base-content/40" />
                            }
                        />
                        <InfoItem
                            label="Website"
                            value={org.website}
                            icon={
                                <MdLanguage className="text-base text-base-content/40" />
                            }
                        />
                    </div>
                </div>
            </div>

            {/* Card Assinatura */}
            {org.subscription && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                    <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-base-content flex items-center gap-2">
                                <MdPayment className="text-secondary" />
                                Assinatura
                            </h3>
                            {!editingSubscription ? (
                                <button
                                    onClick={handleEditSubscription}
                                    className="btn btn-ghost btn-sm gap-1"
                                >
                                    <MdEdit className="text-base" />
                                    Editar
                                </button>
                            ) : (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setEditingSubscription(false)}
                                        className="btn btn-ghost btn-sm"
                                        disabled={savingSubscription}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleSaveSubscription}
                                        className="btn btn-secondary btn-sm gap-1"
                                        disabled={savingSubscription}
                                    >
                                        {savingSubscription ? (
                                            <span className="loading loading-spinner loading-xs" />
                                        ) : (
                                            <MdSave className="text-base" />
                                        )}
                                        Salvar
                                    </button>
                                </div>
                            )}
                        </div>

                        {!editingSubscription ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <InfoItem
                                    label="Plano"
                                    value={org.subscription.plan?.name}
                                />
                                <InfoItem
                                    label="Status"
                                    value={
                                        org.subscription.status === 'ACTIVE'
                                            ? 'Ativo'
                                            : org.subscription.status === 'SUSPENDED'
                                                ? 'Suspenso'
                                                : org.subscription.status === 'CANCELLED'
                                                    ? 'Cancelado'
                                                    : org.subscription.status
                                    }
                                />
                                <InfoItem
                                    label="Dia de Vencimento"
                                    value={String(org.subscription.dueDay)}
                                />
                                <InfoItem
                                    label="Valor"
                                    value={
                                        org.subscription.plan
                                            ? formatPlanPrice(
                                                org.subscription.plan.isCustom &&
                                                    org.subscription.plan.customPrice != null
                                                    ? org.subscription.plan.customPrice
                                                    : org.subscription.plan.price,
                                            )
                                            : '-'
                                    }
                                />
                                <InfoItem
                                    label="Recorrência"
                                    value={
                                        org.subscription.plan?.recurrence
                                            ? RECURRENCE_LABELS[
                                            org.subscription.plan.recurrence as keyof typeof RECURRENCE_LABELS
                                            ] || org.subscription.plan.recurrence
                                            : '-'
                                    }
                                />
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">Plano</span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={subForm.planId}
                                        onChange={(e) =>
                                            setSubForm((prev) => ({
                                                ...prev,
                                                planId: Number(e.target.value),
                                            }))
                                        }
                                    >
                                        {plans.map((plan) => (
                                            <option key={plan.id} value={plan.id}>
                                                {plan.name} -{' '}
                                                {formatPlanPrice(
                                                    plan.isCustom && plan.customPrice != null
                                                        ? plan.customPrice
                                                        : plan.price,
                                                )}{' '}
                                                / {RECURRENCE_LABELS[plan.recurrence] || plan.recurrence}
                                                {plan.private ? ' (Privado)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Dia de Vencimento
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={31}
                                        className="input input-bordered w-full"
                                        value={subForm.dueDay}
                                        onChange={(e) =>
                                            setSubForm((prev) => ({
                                                ...prev,
                                                dueDay: Number(e.target.value),
                                            }))
                                        }
                                    />
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">Status</span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={subForm.status}
                                        onChange={(e) =>
                                            setSubForm((prev) => ({
                                                ...prev,
                                                status: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="ACTIVE">Ativo</option>
                                        <option value="SUSPENDED">Suspenso</option>
                                        <option value="CANCELLED">Cancelado</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Seção Empresas */}
            <div>
                <div className="flex items-center gap-3 mb-4">
                    <h2 className="text-xl font-bold text-base-content flex items-center gap-2">
                        <MdStorefront className="text-secondary" />
                        Empresas
                    </h2>
                    <span className="badge badge-sm badge-secondary">
                        {companies.length}
                    </span>
                </div>

                {companies.length === 0 ? (
                    <div className="bg-base-200/30 rounded-xl p-8 text-center">
                        <MdStorefront className="text-4xl text-base-content/20 mx-auto mb-3" />
                        <p className="text-sm text-base-content/50">
                            Nenhuma empresa vinculada a esta organização.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {companies.map(company => (
                            <div
                                key={company.id}
                                onClick={() =>
                                    navigate(
                                        `/organizations/${org.id}/companies/${company.id}`,
                                    )
                                }
                                className="group bg-gradient-to-br from-base-100 to-base-200/20 rounded-xl border border-base-300/50 shadow-sm hover:shadow-lg hover:border-secondary/30 transition-all duration-300 cursor-pointer overflow-hidden relative"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/0 group-hover:from-secondary/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />

                                <div className="relative p-5">
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow flex-shrink-0">
                                            <MdStorefront className="text-lg text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-base-content group-hover:text-secondary transition-colors line-clamp-1">
                                                {company.name}
                                            </h3>
                                            {company.tradingName && (
                                                <p className="text-xs text-base-content/50 line-clamp-1">
                                                    {company.tradingName}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex-shrink-0">
                                            <div className="w-7 h-7 rounded-lg bg-base-200/50 group-hover:bg-secondary/10 flex items-center justify-center transition-colors">
                                                <MdArrowForward className="text-sm text-base-content/40 group-hover:text-secondary transition-colors" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-1.5 mb-3">
                                        <span
                                            className={`badge badge-xs ${company.active !== false
                                                    ? 'badge-success'
                                                    : 'badge-error'
                                                }`}
                                        >
                                            {company.active !== false
                                                ? 'Ativa'
                                                : 'Inativa'}
                                        </span>
                                        {company.segment && (
                                            <span className="badge badge-xs badge-outline">
                                                {SEGMENT_LABELS[
                                                    company.segment
                                                ] || company.segment}
                                            </span>
                                        )}
                                    </div>

                                    {company.cnpj && (
                                        <p className="text-xs text-base-content/50 font-mono mb-2">
                                            CNPJ: {formatCnpj(company.cnpj)}
                                        </p>
                                    )}

                                    {(company.city || company.state) && (
                                        <p className="text-xs text-base-content/50 mb-2">
                                            {[company.city, company.state]
                                                .filter(Boolean)
                                                .join(' - ')}
                                        </p>
                                    )}

                                    <div className="pt-3 border-t border-base-300/50 flex items-center gap-2">
                                        <MdGroups className="text-base text-base-content/40" />
                                        <span className="text-xs text-base-content/50">
                                            {company._count?.users ?? 0}{' '}
                                            {(company._count?.users ?? 0) === 1
                                                ? 'usuário'
                                                : 'usuários'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

function InfoItem({
    label,
    value,
    icon,
}: {
    label: string
    value?: string | null
    icon?: React.ReactNode
}) {
    return (
        <div className="flex items-start gap-3 p-3 bg-base-200/30 rounded-xl">
            {icon && <div className="mt-0.5">{icon}</div>}
            <div className="flex-1 min-w-0">
                <p className="text-xs text-base-content/60 mb-0.5">{label}</p>
                <p className="text-sm font-medium text-base-content">
                    {value || '-'}
                </p>
            </div>
        </div>
    )
}
